import React, { useEffect } from "react";
import Select from "react-select";
import { calculateRentalDuration, comma, uncomma } from "@/utils";
import { chargeRateOptions } from "@/constants/selectOptions";
import { useGetRentPriceTableQuery, useLazyGetRentPriceTableQuery } from "@/redux/metaApi";
import { sumRentalDurations, calculateRentPriceWithGroupBasePrice } from "@/utils/calculator";

const DISABLED_STATUSES = ["SIGN_CONFIRM", "RETURN_FINISH", "FINISH", "CHARGE_FINISH", "PRE_CHARGE_FINISH"];

export const TotalBillingInfo = ({ formData, handleChange, setFormData }) => {
    const { contracts, contractInsurance } = formData;

    useEffect(() => {
        let totalDays = 0;
        let totalHours = 0;
        let totalRentFee = 0;
        let totalChargeFee = 0;
        let totalFormula = "";
        let representativeBasePrice = 0;
        let isValid = false;
        let baseDivideHours = 24;
        let totalMinutesSum = 0;

        if (Array.isArray(contracts) && contracts.length > 0) {
            // contractGroup 전체 대여기간 합산
            const { days: groupDays, hours: groupHours } = sumRentalDurations(contracts);

            // 대표 기준요금 산출 (첫번째 계약의 요금표 기준)
            if (contracts[0]?.billingInfo?.rentPrice?.rentPriceItemDetails) {
                const rentPriceItemDetails = contracts[0].billingInfo.rentPrice.rentPriceItemDetails;
                let currentRateRow =
                    groupDays >= 7
                        ? rentPriceItemDetails.find((item) => item.title === "7일이상")
                        : rentPriceItemDetails.find(
                              (item) => item.startHour <= groupDays * 24 + groupHours && item.endHour > groupDays * 24 + groupHours
                          );
                representativeBasePrice = currentRateRow?.price || 0;
            }

            // 각 계약별 요금 합산
            contracts.forEach((contract) => {
                const { allocation } = contract;
                if (allocation?.releasedAt && allocation?.returnedAt && contract?.billingInfo?.rentPrice?.rentPriceItemDetails) {
                    isValid = true;
                    const { days, hours, totalMinutes } = calculateRentalDuration(new Date(allocation.releasedAt), new Date(allocation.returnedAt));
                    // groupBasePrice로 요금 계산
                    const chargeRate = contractInsurance?.chargeRate || 0;
                    const platformCommissionRate = formData.platformCommissionRate || 8.0;
                    const result = calculateRentPriceWithGroupBasePrice(
                        representativeBasePrice,
                        days,
                        hours,
                        chargeRate,
                        platformCommissionRate
                    );
                    totalDays += days;
                    totalHours += hours;
                    totalRentFee += result.rentFee;
                    totalChargeFee += result.chargeFee;
                    totalMinutesSum += totalMinutes;
                }
            });

            if (isValid) {

                if(totalMinutesSum < (60 * 10)) {
                    baseDivideHours = 10;
                } else if(totalMinutesSum < (60 * 12)) {
                    baseDivideHours = 12;
                } else {
                    baseDivideHours = 24;
                }

                if (totalHours >= 24) {
                    const extraDays = Math.floor(totalHours / 24);
                    totalDays += extraDays;
                    totalHours = totalHours % 24;
                }


                const chargeRate = contractInsurance?.chargeRate / 100 || 0;
                totalRentFee = representativeBasePrice * totalDays + representativeBasePrice * (totalHours / baseDivideHours);
                totalChargeFee = Math.floor(totalRentFee * chargeRate);

                totalFormula = `((${comma(representativeBasePrice)} * ${totalDays}) + (${comma(
                    representativeBasePrice
                )} * (${totalHours} / ${baseDivideHours}))) * ${chargeRate} = ${comma(totalChargeFee)}`;

            } else {
                totalDays = 0;
                totalHours = 0;
                totalRentFee = 0;
                totalChargeFee = 0;
                totalFormula = "";
            }

            setFormData((prev) => ({
                ...prev,
                contractInsurance: {
                    ...prev.contractInsurance,
                    totalRentalPeriod: isValid ? `${totalDays}일 ${totalHours}시간` : "",
                    days: isValid ? totalDays : "",
                    hours: isValid ? totalHours : "",
                    totalRentFee: isValid ? totalRentFee : "",
                    totalChargeFee: isValid ? totalChargeFee : "",
                    totalFormula: isValid ? totalFormula : "",
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                contractInsurance: {
                    ...prev.contractInsurance,
                    totalRentalPeriod: "",
                    days: "",
                    hours: "",
                    totalRentFee: "",
                    totalChargeFee: "",
                    totalFormula: "",
                },
            }));
        }
    }, [contracts, setFormData, contractInsurance?.chargeRate, formData.platformCommissionRate]);

    // 요금표 목록 조회
    const { data: rentPriceTableData } = useGetRentPriceTableQuery();
    const rentPriceTableOptions = [
        { value: "INSURANCE_BASE", label: "보험사기준" },
        ...(rentPriceTableData && rentPriceTableData.data
            ? rentPriceTableData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : []),
        { value: "INPERSON", label: "직접입력" },
    ];

    // lazy hook 선언: INSURANCE_BASE 선택 시 API 호출에 사용
    const [triggerGetRentPriceTable] = useLazyGetRentPriceTableQuery();

    // 청구방법, 청구율 비활성화 조건
    const isSelectDisabled = !!formData.orderId || DISABLED_STATUSES.includes(formData.contractStatus);

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        청구 방법 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {formData.orderId ? (
                            <Select
                                options={rentPriceTableOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable
                                placeholder="청구 방법"
                                value={rentPriceTableOptions?.find((opt) => opt.value === formData.contractInsurance?.chargeMethod)}
                                onChange={(option) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        contractInsurance: {
                                            ...prev.contractInsurance,
                                            chargeMethod: option.value,
                                        },
                                    }));
                                }}
                                // isDisabled={isSelectDisabled}
                                isDisabled
                            />
                        ) : (
                            <Select
                                options={rentPriceTableOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                isSearchable
                                placeholder={
                                    !formData.contractInsurance?.insuranceCorpId ? "보험사를 먼저 선택해주세요." : "청구 방식 선택"
                                }
                                // isDisabled={!formData.contractInsurance?.insuranceCorpId}
                                //isDisabled
                                value={
                                    contractInsurance?.chargeMethod === "INSURANCE_BASE"
                                        ? rentPriceTableOptions.find((opt) => opt.value === "INSURANCE_BASE")
                                        : rentPriceTableOptions.find((opt) => opt.value === contractInsurance?.rentPriceTableId)
                                }
                                onChange={(option) => {
                                    if (option.value === "INSURANCE_BASE") {
                                        if (!formData.contractInsurance?.insuranceCorpId) {
                                            setFormData((prev) => ({
                                                ...prev,
                                                toastMsg: "보험사를 먼저 선택해주세요.",
                                            }));
                                            return;
                                        } else {
                                            // INSURANCE_BASE 선택 시, getRentPriceTable API 호출
                                            triggerGetRentPriceTable({
                                                rent_price_type: "INSURANCE_BASE",
                                            })
                                                .unwrap()
                                                .then((response) => {
                                                    // response.data 배열에서 matching insuranceCorp.id 찾기
                                                    const matched = response.data.find(
                                                        (item) =>
                                                            item.insuranceCorp &&
                                                            item.insuranceCorp.id === formData.contractInsurance?.insuranceCorpId
                                                    );
                                                    if (matched) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            contractInsurance: {
                                                                ...prev.contractInsurance,
                                                                chargeMethod: "INSURANCE_BASE",
                                                                rentPriceTableId: matched.id,
                                                            },
                                                        }));
                                                    } else {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: "해당 보험사에 맞는 요금표가 없습니다.",
                                                        }));
                                                    }
                                                })
                                                .catch((error) => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        toastMsg: error?.message || "요금표 조회에 실패하였습니다.",
                                                    }));
                                                });
                                        }
                                    } else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            contractInsurance: {
                                                ...prev.contractInsurance,
                                                chargeMethod: "RENT_PRICE",
                                                rentPriceTableId: option.value,
                                            },
                                        }));
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>
                        청구율 <em>*</em>
                    </span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={chargeRateOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable
                            placeholder="청구율"
                            value={chargeRateOptions?.find((opt) => opt.value === contractInsurance?.chargeRate)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    contractInsurance: {
                                        ...prev.contractInsurance,
                                        chargeRate: option.value,
                                    },
                                }));
                            }}
                            isDisabled={isSelectDisabled}
                        />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            {!formData.orderId && (
                <>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 렌트 시간</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalRentalPeriod"
                                    placeholder=""
                                    value={contractInsurance?.totalRentalPeriod || ""}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 렌트 요금</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalRentFee"
                                    placeholder=""
                                    value={contractInsurance?.totalRentFee ? comma(contractInsurance.totalRentFee) : ""}
                                    disabled
                                />
                                <span className="input-esset">원</span>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="3">
                        <div className="row-head">
                            <span>총 청구 요금</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input
                                    type="text"
                                    name="totalChargeFee"
                                    placeholder=""
                                    value={contractInsurance?.totalChargeFee ? comma(contractInsurance.totalChargeFee) : ""}
                                    disabled
                                />
                                <span className="input-esset">원</span>
                            </div>
                        </div>
                    </div>
                    <div className="item-row" data-grow="1">
                        <div className="row-head">
                            <span>청구 수식</span>
                        </div>
                        <div className="row-content">
                            <div className="form-element">
                                <input type="text" name="formula" placeholder="" value={contractInsurance?.totalFormula || ""} disabled />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
